<template>
  <v-container class="pa-0 mb-8 payment-container">
    <NavHeader
      :title="$t('screens.payment.headerTitle')"
      :subtitle="
        $store.state.restaurant.type === 'restaurant'
          ? $t('screens.payment.headerSubtitle', { tableName })
          : $t('screens.payment.headerSubtitleWithoutTable', { tableName })
      "
      @onBackClick="goBack"
    />
    <LayoutCard v-if="error" rounded>
      <StateScreen
        :title="errorResponseTitle"
        :subtitle="errorResponseDesc"
        :image="require(`@/assets/images/order-failed-image.svg`)"
      />
    </LayoutCard>
    <div v-if="isInitialLoad && !error" class="d-flex justify-center pt-8">
      <v-progress-circular indeterminate color="black" />
    </div>
    <div v-show="!isInitialLoad && !error">
      <v-alert
        v-if="showSolidgateNotice"
        border="left"
        colored-border
        color="orange darken-2"
        icon="mdi-alert-circle-outline"
        class="mx-4 mt-4 mb-4"
        data-cy="solidgate-payment-notice"
      >
        <div class="tp-text-subtitle-semibold">
          {{ $t('screens.payment.solidgateNoticeTitle') }}
        </div>
        <div class="tp-text-body" style="white-space: pre-line">
          {{ $t('screens.payment.solidgateNoticeText') }}
        </div>
      </v-alert>
      <TipPersonnel
        v-if="showTipsPicker"
        :amount="amount || userSubtotalAmount"
        :unique-order-number="uniqueOrderNumber"
        :order-personnel="orderPersonnel"
        @on-change="changeTips"
      />
      <LayoutCard :title="$t('screens.payment.summaryTitle')" rounded>
        <PriceSummary
          :subtotal-amount="subtotal"
          :total-amount="totalAmount"
          :transaction-fee="transactionFee"
          :tips-amount="tipsAmount"
          :discount-amount="discountAmount"
          :coupon-code="couponCode"
          :small-order-fee="smallOrderFee"
          :amount-being-paid="payGoRef ? amount : null"
          :hide-subtotal="payGoRef"
        />
      </LayoutCard>
      <LegalEntitiesInvoiceRequest
        v-model="invoiceRequested"
        :country-code="countryCode"
      />
      <form id="payment-form">
        <LayoutCard rounded :title="$t('screens.payment.paymentMethods')">
          <PaymentMethods
            ref="paymentMethods"
            :wallet-available="walletAvailable"
            :is-loading-pr-button="isLoading"
            :client-secret="stripePaymentData?.clientSecret"
            :preselected-payment-method="presentPaymentMethod"
            :solid-disabled="solidPaymentsBlocked"
            @on-change="onPaymentMethodChange"
            @on-success="() => onPaymentSuccess(uuid)"
            @on-error="onPaymentError"
          />
        </LayoutCard>
        <div>
          <v-alert
            v-if="errorMessage"
            border="left"
            colored-border
            color="red accent-4"
            class="mt-6"
            dismissible
            close-icon="mdi-close"
            >{{ errorMessage }}</v-alert
          >
          <div id="payment-message" class="hidden"></div>
          <v-container class="px-10">
            <div v-show="showPayCta">
              <Button
                ref="CTAButton"
                dark
                :loading="ctaLoading"
                :disabled="ctaDisabled"
                data-cy="pay-cta"
                @click="onPayCtaPress"
              >
                <div class="menu-item-sheet-controls__add-btn">
                  <div>
                    {{ payCtaLabel }}
                  </div>
                  <div>
                    {{ totalAmountFormatted }}
                  </div>
                </div>
              </Button>
            </div>
            <TermsAndConditions />
          </v-container>
        </div>
      </form>
      <v-overlay :value="backdropVisible">
        <div class="d-flex flex-column justify-center align-center">
          <lottie-vue-player
            :src="backdrop.image"
            style="width: 100px; height: 100px; background-color: transparent"
            autoplay
            loop
          />
          <p class="tp-text-subtitle-bold white--text pt-6">
            {{ $t(backdrop.text) }}
          </p>
        </div>
      </v-overlay>
    </div>
    <PisWarning
      ref="pisWarning"
      @pay="navigateToPaymentLink(neopayPaymentLink)"
    />
    <PaymentBottomSheetError
      ref="paymentBottomSheetError"
      @paymentErrorBottomSheetCtaPress="generateSolidPaymentIntent"
    />
  </v-container>
</template>

<script type="application/javascript" src="https://js.stripe.com/v3/"></script>

<script>
import NavHeader from '@/components/Navigation/NavHeader.vue';
import LayoutCard from '@/components/LayoutCard.vue';
import Button from '@/components/Button.vue';
import TermsAndConditions from '@/components/common/TermsAndConditions.vue';
import PaymentMethods from '@/components/Payments/PaymentMethods.vue';
import PriceSummary from '@/components/common/PriceSummary.vue';
import TipPersonnel from '@/components/Tips/TipPersonnel.vue';
import StateScreen from '@/components/StateScreen.vue';
import PisWarning from '@/components/Payments/PisWarning.vue';
import PaymentBottomSheetError from '@/components/Payments/PaymentBottomSheetError.vue';
import LegalEntitiesInvoiceRequest from '@/components/Payments/LegalEntitiesInvoiceRequest.vue';

import { PAYMENT_METHODS } from '@/utils/constants';
import { parseQueryParam } from '@/utils/strings';

import { createStripeElements } from '@/utils/stripeElements';
import { PRESENCE_STATES } from '@/store/modules/orderPresence';

const BACKDROPS = {
  success: {
    text: 'screens.payment.paymentSuccessful',
    image: '/assets/order-status-animations/successful-payment.json',
  },
  successCash: {
    text: 'screens.payment.orderPlacedCtaLabel',
    image: '/assets/order-status-animations/successful-payment.json',
  },
  loading: {
    text: 'screens.payment.paymentInProgress',
    image: '/assets/order-status-animations/loading.json',
  },
  error: {
    text: 'screens.payment.paymentFailed',
    image: '/assets/order-status-animations/failed-payment.json',
  },
  loadingGeneric: {
    image: '/assets/order-status-animations/loading.json',
  },
};

export default {
  components: {
    LayoutCard,
    Button,
    NavHeader,
    TermsAndConditions,
    PaymentMethods,
    PriceSummary,
    TipPersonnel,
    StateScreen,
    PisWarning,
    PaymentBottomSheetError,
    LegalEntitiesInvoiceRequest,
  },
  metaInfo: {
    script: [{ src: 'https://js.stripe.com/v3/', async: false, defer: false }],
  },
  data() {
    return {
      amount: null,
      stripe: undefined,
      hasPaymentIntent: false,
      walletAvailable: false,
      payByCardDisabled: false,
      subtotal: null,
      totalAmount: null,
      transactionFee: null,
      tipsAmount: null,
      discountAmount: null,
      couponCode: null,
      smallOrderFee: null,
      isLoading: false,
      isInitialLoad: true,
      backdropVisible: false,
      backdrop: {},
      error: null,
      errorMessage: null,
      uniqueOrderNumber: null,
      montonioPaymentLink: null,
      neopayPaymentLink: null,
      neopayBlikPaymentLink: null,
      mollieIdealPaymentLink: null,
      paymentMethod: null,
      stripePaymentData: null,
      montonioPaymentData: null,
      neopayPaymentData: null,
      neopayBlikPaymentData: null,
      mollieIdealPaymentData: null,
      cashPaymentData: null,
      roomBillPaymentData: null,
      elements: null,
      expressCheckoutElement: null,
      errorResponseTitle: null,
      errorResponseDesc: null,
      expiresAt: null,
      uuid: null,
      periodicCheckTimer: null,
      tipAmount: undefined,
      personnelId: null,
      userSubtotalAmount: null,
      invoiceRequested: false,
      presenceOrderNumber: null,
      presenceOwnerId: `presence-${Date.now()}-${Math.random()}`,
      paymentAttemptInFlight: false,
    };
  },
  created() {
    document.addEventListener(
      'visibilitychange',
      this.checkIfPaymentSessionExpired
    );
    window.addEventListener('focus', this.resetLoadingOnFocus);
  },
  beforeDestroy() {
    document.removeEventListener(
      'visibilitychange',
      this.checkIfPaymentSessionExpired
    );
    window.removeEventListener('focus', this.resetLoadingOnFocus);
    clearInterval(this.periodicCheckTimer);

    // A provider redirect unloads the page instead of destroying the component,
    // so getting here without an attempt in flight means the guest navigated
    // away inside the app and gave up on paying. An attempt that is still in
    // flight keeps its presence, which is what the server holds on to while a
    // bank has the guest on its own website.
    if (this.presenceOrderNumber) {
      this.$store.dispatch('leaveOrderPresence', {
        uniqueOrderNumber: this.presenceOrderNumber,
        ownerId: this.presenceOwnerId,
        keepServerPresence: this.paymentAttemptInFlight,
      });
    }
  },
  computed: {
    invoiceRequestedQueryParam() {
      return (
        this.invoiceRequested && { invoiceRequested: this.invoiceRequested }
      );
    },
    offlinePayment() {
      return [PAYMENT_METHODS.CASH, PAYMENT_METHODS.ROOM_BILL].includes(
        this.paymentMethod
      );
    },
    showPayCta() {
      return [
        PAYMENT_METHODS.MOLLIE_IDEAL,
        PAYMENT_METHODS.STRIPE,
        PAYMENT_METHODS.NEOPAY,
        PAYMENT_METHODS.NEOPAY_BLIK,
        PAYMENT_METHODS.CASH,
        PAYMENT_METHODS.ROOM_BILL,
        PAYMENT_METHODS.SOLID_CARD,
      ].includes(this.paymentMethod);
    },
    payCtaLabel() {
      if (this.offlinePayment) {
        return this.$t('screens.payment.placeOrderCtaLabel');
      }

      return this.$t('screens.payment.payCtaLabel');
    },
    ctaDisabled() {
      if (this.paymentMethod === PAYMENT_METHODS.STRIPE) {
        return this.payByCardDisabled;
      } else if (
        [
          PAYMENT_METHODS.MONTONIO,
          PAYMENT_METHODS.NEOPAY,
          PAYMENT_METHODS.NEOPAY_BLIK,
          PAYMENT_METHODS.MOLLIE_IDEAL,
          PAYMENT_METHODS.CASH,
          PAYMENT_METHODS.ROOM_BILL,
          PAYMENT_METHODS.SOLID_CARD,
        ].includes(this.paymentMethod)
      ) {
        return false;
      }

      return true;
    },
    tableName() {
      return this.$store.state.table.name;
    },
    returnUrl() {
      const payGoRedirectUrl = `${process.env.VUE_APP_BASE_URL}/success/${this.$store.state.session}?orderNumber=${this.uniqueOrderNumber}${this.invoiceRequestedQueryParam}`;
      const orderRedirectUrl = `${process.env.VUE_APP_BASE_URL}/menu/${this.$store.state.session}?order=${this.uniqueOrderNumber}`;

      if (this.orderEnabled && this.payGoEnabled && this.payGoRef) {
        return payGoRedirectUrl;
      } else if (this.orderEnabled && this.payGoEnabled) {
        return orderRedirectUrl;
      } else if (this.payGoEnabled) {
        return payGoRedirectUrl;
      }

      return orderRedirectUrl;
    },
    totalAmountFormatted() {
      return this.$store.getters.getFormattedPrice(this.totalAmount);
    },
    payGoEnabled() {
      return this.$store.getters.functionalityPayGoEnabled;
    },
    payGoRef() {
      const ref = this.$route.query.ref;
      return ref === 'select-items' || ref === 'order';
    },
    orderEnabled() {
      return this.$store.getters.functionalityOrderEnabled;
    },
    ordersDisabled() {
      return !this.orderEnabled && !this.payGoEnabled;
    },
    orderAlreadyPaid() {
      return this.$store.getters.getIsOrderAlreadyPaid(
        this.$route.params.order
      );
    },
    posOrder() {
      return this.$store.getters.getPOSOrderByUniqueOrderNumber(
        this.$route.params.order
      );
    },
    orderPersonnel() {
      return this.posOrder?.personnel;
    },
    orderHasPersonnel() {
      return !!this.orderPersonnel;
    },
    showTipsPicker() {
      // If hybrid mode is enabled, we should not use functionalitySendTipsToPosWaiterEnabled
      if (this.orderEnabled && this.payGoEnabled && !this.payGoRef) {
        return false;
      }
      // Tips are selected in-app and routed by the restaurant/backoffice.
      if (this.$store.getters.functionalityTipsManagedByRestaurantEnabled) {
        return this.payGoEnabled;
      }
      if (this.$store.getters.functionalitySendTipsToPosWaiterEnabled) {
        return this.canTip && this.payGoEnabled && this.orderHasPersonnel;
      }
      return this.canTip && this.payGoEnabled;
    },
    countryCode() {
      return this.$store.getters.restaurantCountryCode;
    },
    stripeEnabled() {
      return this.$store.getters.paymentProviderStripeEnabled;
    },
    solidEnabled() {
      return this.$store.getters.paymentProviderSolidEnabled;
    },
    mollieIdealEnabled() {
      return this.$store.getters.paymentProviderMollieIdealEnabled;
    },
    isSamsungInternet() {
      const userAgent = navigator.userAgent || '';
      return /SamsungBrowser/i.test(userAgent) && /Android/i.test(userAgent);
    },
    presentPaymentMethod() {
      if (this.isSamsungInternet) {
        return '';
      }

      if (this.mollieIdealEnabled) {
        this.paymentMethod = PAYMENT_METHODS.MOLLIE_IDEAL;
        return PAYMENT_METHODS.MOLLIE_IDEAL;
      }
      if (this.solidEnabled && !this.solidPaymentsBlocked) {
        return PAYMENT_METHODS.SOLID_WALLET;
      }
      return '';
    },
    ctaLoading() {
      return this.isLoading;
    },
    canTip() {
      return this.$store.getters.canTip;
    },
    isPayGoFlow() {
      if (this.orderEnabled && this.payGoEnabled) return !!this.payGoRef;
      return this.payGoEnabled;
    },
    solidPaymentsBlocked() {
      // Incident switch for Greet Pay only; toggled per environment via Vercel.
      // While on, Solidgate methods are shown but cannot be selected or paid with.
      return (
        this.isPayGoFlow &&
        this.solidEnabled &&
        process.env.VUE_APP_PAYGO_SOLIDGATE_NOTICE_ENABLED === 'true'
      );
    },
    showSolidgateNotice() {
      return this.solidPaymentsBlocked;
    },
  },
  watch: {
    async orderAlreadyPaid(isPaid) {
      if (isPaid && this.payGoEnabled && !this.orderEnabled) {
        await this.navigateToOrderDirectly(this.uniqueOrderNumber);
      }
    },
    async tipAmount(newTipAmount, oldTipAmount) {
      if (newTipAmount !== oldTipAmount) {
        const personnelId = this.personnelId;
        const tipAmount = newTipAmount || 0;

        this.isLoading = true;

        await this.handlePaymentIntents(personnelId, tipAmount);

        this.isInitialLoad = false;
        this.isLoading = false;

        this.checkIfPaymentSessionExpired();
      }
    },
  },
  async mounted() {
    this.isInitialLoad = true;
    const sessionToken = this.$route.params.session;

    // amount that is used to calculate tip chips
    const userSubtotalAmount = parseQueryParam(
      this.$route.query.userSubtotalAmount
    );

    if (userSubtotalAmount) {
      this.userSubtotalAmount = parseFloat(userSubtotalAmount);
    }

    await this.$store.dispatch('fetchSessionData', { session: sessionToken });
    await this.$store.dispatch('fetchOrders');

    if (this.ordersDisabled) {
      this.$router.push({
        name: 'Menu',
        params: { session: sessionToken },
      });
      return;
    }

    if (this.orderAlreadyPaid) {
      return this.navigateToOrderDirectly(this.uniqueOrderNumber);
    }

    if (!this.posOrder && this.payGoEnabled) {
      await this.$store.dispatch('fetchOrdersFromPOS');
    }

    if (this.payGoEnabled && this.$route.params.order) {
      this.presenceOrderNumber = this.$route.params.order;
      this.$store.dispatch('joinOrderPresence', {
        uniqueOrderNumber: this.presenceOrderNumber,
        state: PRESENCE_STATES.PAYING,
        ownerId: this.presenceOwnerId,
      });
    }

    // ASG INTEGRATION
    // this.$store.dispatch('subscribeToExternalPaymentStatus', {
    //   onSuccess: this.onPaymentSuccess,
    //   onError: () => this.showBackdrop('error', 2000),
    // });

    if (!this.showTipsPicker) {
      await this.handlePaymentIntents();
      this.isInitialLoad = false;
      this.checkIfPaymentSessionExpired();
    }

    this.periodicCheckTimer = setInterval(() => {
      this.checkIfPaymentSessionExpired();
    }, 10000);
  },
  methods: {
    async navigateToOrderDirectly(orderNumber) {
      this.$router.push({
        name: 'Order',
        params: { session: this.$store.state.session },
        query: { orderNumber },
      });
      await this.$store.dispatch('fetchOrders');
      await this.$store.dispatch('openOrder', orderNumber);
    },
    resetLoadingOnFocus() {
      this.isLoading = false;
    },
    async loadStripeIfNeeded() {
      if (this.stripe || !this.stripeEnabled) {
        return;
      }

      this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PK || '');
    },
    async changeTips({ personnelId, tipAmount = 0 }) {
      this.personnelId = personnelId;
      this.tipAmount = tipAmount;
    },
    applyIntentData(data) {
      this.hasPaymentIntent = true;
      this.expiresAt = data.expires_at;
      this.subtotal = data.subtotal;
      this.totalAmount = data.total;
      this.transactionFee = data.transaction_fee;
      this.tipsAmount = data.tips;
      this.discountAmount = data.discount;
      this.couponCode = data.coupon;
      this.uniqueOrderNumber = data.unique_order_number;
      this.smallOrderFee = data?.small_order_fee || 0;
      this.amount = data.amount;
    },
    async loadStripeElements() {
      if (this.error || !this.stripeEnabled) return;

      const result = await createStripeElements(this.stripe, {
        clientSecret: this.stripePaymentData.clientSecret,
        locale: this.countryCode,
        restaurantName: this.$store.state.restaurant.name,
        cardRef: this.$refs?.paymentMethods?.$refs?.card,
        prButtonRef: this.$refs?.paymentMethods?.$refs?.prButton,
        returnUrl: this.returnUrl,
        onConfirmSuccess: () =>
          this.onPaymentSuccess(this.stripePaymentData?.uuid),
        onConfirmError: error => {
          this.$refs?.paymentBottomSheetError?.open(error);
          this.showMessage(error.message);
          this.$store.dispatch('sendAnalyticsEvent', {
            event: 'purchase_failed',
            transaction_id: this.uniqueOrderNumber,
            message: error.message,
          });
        },
      });

      this.elements = result.elements;
      this.expressCheckoutElement = result.expressCheckout;
      this.walletAvailable = result.walletAvailable;
      this.payByCardDisabled = result.payByCardDisabled;
    },
    navigateToPaymentLink(link) {
      if (link) {
        this.isLoading = true;
        this.paymentAttemptInFlight = true;
        window.location = link;
      }
    },
    async generateSolidPaymentIntent({
      personnelId = null,
      tipAmount = 0,
      session = this.$store.state.session,
    } = {}) {
      if (!this.solidEnabled) {
        return;
      }

      const dispatch = this.$store.dispatch;
      const order = this.$route.params.order;
      const payForSelectedItems =
        this.$route.query.payForSelectedItems || false;

      await dispatch('generateSolidGatePaymentIntent', {
        session,
        order,
        personnelId,
        tips: tipAmount * 100,
        payForSelectedItems,
      });

      const intentData = this.$store.getters.solidGatePaymentIntent;

      if (!intentData) {
        return;
      }

      this.applyIntentData(intentData);
      this.uuid = intentData.uuid;
    },
    async generateStripePaymentIntent({
      personnelId = null,
      tipAmount = 0,
      session = this.$store.state.session,
    } = {}) {
      if (!this.stripeEnabled) {
        return;
      }

      try {
        const paymentRequest = await this.axios.post(
          `/payments/stripe/payment-intent`,
          {
            session,
            order: this.$route.params.order,
            personnel_id: personnelId,
            tips: tipAmount * 100,
            pay_for_selected_items:
              this.$route.query.payForSelectedItems || false,
          }
        );
        if (paymentRequest.data.total) {
          this.stripePaymentData = paymentRequest.data;
          this.applyIntentData(this.stripePaymentData);
        } else {
          this.error = true;
        }
        return;
      } catch (error) {
        this.handleError(error);
      }
    },
    async generateCashPaymentIntent({
      personnelId = null,
      tipAmount = 0,
      session = this.$store.state.session,
    } = {}) {
      const cashEnabled = this.$store.getters.paymentProviderCashEnabled;
      if (!cashEnabled) {
        return;
      }

      try {
        const paymentRequest = await this.$store.dispatch(
          'createCashPaymentIntent',
          {
            session,
            order: this.$route.params.order,
            personnelId,
            tips: tipAmount * 100,
            payForSelectedItems: this.$route.query.payForSelectedItems || false,
          }
        );
        if (paymentRequest.data.total) {
          this.cashPaymentData = paymentRequest.data;
          this.applyIntentData(this.cashPaymentData);
        } else {
          this.error = true;
        }
        return;
      } catch (error) {
        this.handleError(error);
      }
    },
    async generateRoomBillPaymentIntent({
      personnelId = null,
      tipAmount = 0,
      session = this.$store.state.session,
    } = {}) {
      const roomBillEnabled = this.$store.getters
        .paymentProviderRoomBillEnabled;
      if (!roomBillEnabled) {
        return;
      }

      try {
        const paymentRequest = await this.$store.dispatch(
          'createRoomBillPaymentIntent',
          {
            session,
            order: this.$route.params.order,
            personnelId,
            tips: tipAmount * 100,
            payForSelectedItems: this.$route.query.payForSelectedItems || false,
          }
        );
        if (paymentRequest.data.total) {
          this.roomBillPaymentData = paymentRequest.data;
          this.applyIntentData(this.roomBillPaymentData);
        } else {
          this.error = true;
        }
        return;
      } catch (error) {
        this.handleError(error);
      }
    },
    async createMontonioOrder({
      personnelId = null,
      tipAmount = 0,
      session = this.$store.state.session,
    } = {}) {
      const montonioEnabled = this.$store.getters
        .paymentProviderMontonioEnabled;
      if (montonioEnabled) {
        try {
          const paymentRequest = await this.axios.post(
            `/payments/montonio/create-order`,
            {
              session,
              order: this.$route.params.order,
              personnel_id: personnelId,
              pay_for_selected_items:
                this.$route.query.payForSelectedItems || false,
              tips: tipAmount * 100,
            }
          );

          this.montonioPaymentData = paymentRequest.data;
          this.montonioPaymentLink = paymentRequest.data.redirect_url;
          return;
        } catch (error) {
          this.handleError(error);
        }
      }
    },
    async createNeopayTransaction({
      personnelId = null,
      tipAmount = 0,
      session = this.$store.state.session,
    } = {}) {
      const neopayEnabled = this.$store.getters.paymentProviderNeopayEnabled;
      if (neopayEnabled) {
        try {
          const paymentRequest = await this.axios.post(
            `/payments/neopay/create-transaction`,
            {
              session,
              order: this.$route.params.order,
              personnel_id: personnelId,
              pay_for_selected_items:
                this.$route.query.payForSelectedItems || false,
              tips: tipAmount * 100,
            }
          );

          this.neopayPaymentData = paymentRequest.data;
          this.neopayPaymentLink = paymentRequest.data.redirect_url;
          return;
        } catch (error) {
          this.handleError(error);
        }
      }
    },
    async createNeopayBlikTransaction({
      personnelId = null,
      tipAmount = 0,
      session = this.$store.state.session,
    } = {}) {
      const neopayBlikEnabled = this.$store.getters
        .paymentProviderNeopayBlikEnabled;
      if (neopayBlikEnabled) {
        try {
          const paymentRequest = await this.axios.post(
            `/payments/neopay/create-blik-transaction`,
            {
              session,
              order: this.$route.params.order,
              personnel_id: personnelId,
              pay_for_selected_items:
                this.$route.query.payForSelectedItems || false,
              tips: tipAmount * 100,
            }
          );

          this.neopayBlikPaymentData = paymentRequest.data;
          this.neopayBlikPaymentLink = paymentRequest.data.redirect_url;
          return;
        } catch (error) {
          this.handleError(error);
        }
      }
    },
    async createMollieIdealPayment({
      personnelId = null,
      tipAmount = 0,
      session = this.$store.state.session,
    } = {}) {
      const mollieIdealEnabled = this.$store.getters
        .paymentProviderMollieIdealEnabled;
      if (mollieIdealEnabled) {
        try {
          const paymentRequest = await this.$store.dispatch(
            'createMollieIdealPayment',
            {
              session,
              order: this.$route.params.order,
              personnelId,
              tips: tipAmount * 100,
              payForSelectedItems:
                this.$route.query.payForSelectedItems || false,
            }
          );

          this.mollieIdealPaymentData = paymentRequest.data;
          this.mollieIdealPaymentLink = paymentRequest.data.redirect_url;

          if (!this.subtotal) {
            this.subtotal = this.mollieIdealPaymentData.subtotal;
            this.totalAmount = parseFloat(this.mollieIdealPaymentData.total);
            this.transactionFee = this.mollieIdealPaymentData.transaction_fee;
            this.tipsAmount = this.mollieIdealPaymentData.tips;
            this.smallOrderFee =
              this.mollieIdealPaymentData?.small_order_fee || 0;
          }

          return;
        } catch (error) {
          this.handleError(error);
        }
      }
    },
    handleError(error) {
      if (error.response.data.message === 'Order is already paid') {
        this.error = true;
        return this.navigateToOrderDirectly(this.$route.params.order);
      } else if (
        error.response.data.message ===
        'Order already has payments of other type'
      ) {
        this.error = true;
        this.errorResponseTitle = this.$t(
          'screens.payment.error.partAlreadyPaid.title'
        );
        this.errorResponseDesc = this.$t(
          'screens.payment.error.partAlreadyPaid.description'
        );
      }
    },
    async payByCard(e) {
      e.preventDefault();

      this.isLoading = true;
      this.payByCardDisabled = true;
      this.showBackdrop('loading');

      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: this.returnUrl,
        },
      });

      if (error.type === 'card_error' || error.type === 'validation_error') {
        this?.$refs?.paymentBottomSheetError?.open(error);
        this.showMessage(error.message);

        this.$store.dispatch('sendAnalyticsEvent', {
          event: 'purchase_failed',
          transaction_id: this?.uniqueOrderNumber,
          message: error.message,
        });
      } else {
        this?.$refs?.paymentBottomSheetError?.open(error);
        this.showMessage('An unexpected error occurred.');
      }

      if (!error) {
        this.sendPurchaseAnalytics('card');
      }

      this.isLoading = false;
      this.payByCardDisabled = false;
      this.hideBackdrop();
    },
    goBack() {
      this.$router.go(-1);
    },
    showMessage(message) {
      this.errorMessage = message;
    },
    async showBackdrop(backdrop, timeout) {
      await this.hideBackdrop(); // hide previous backdrop
      this.backdropVisible = true;
      this.backdrop = BACKDROPS[backdrop];

      if (timeout) {
        return new Promise(resolve => setTimeout(resolve, timeout));
      }
    },
    hideBackdrop() {
      this.backdropVisible = false;
      this.backdrop = {};
    },
    async onPaymentSuccess(paymentUuid) {
      if (this.offlinePayment) {
        await this.showBackdrop('successCash', 2000);
      } else {
        await this.showBackdrop('loadingGeneric', 1);
      }

      this.sendPurchaseAnalytics('wallet');

      if (!paymentUuid) {
        console.error('onPaymentSuccess called without paymentUuid');
        this.hideBackdrop();
        return;
      }

      const origin = this.isPayGoFlow ? 'pay' : 'order';

      if (!this.isPayGoFlow) {
        // The order is paid: its tip must not linger in the cart and leak
        // into a later pay&go payment in the same session.
        this.$store.dispatch('setTipAmount', null);
      }

      this.navigateToPaymentStatus({ paymentUuid, origin });
    },
    onPaymentError(error) {
      const errorCodeMap = {
        3.02: 'insufficient_funds',
      };

      const adoptedError = {
        ...error,
        decline_code: errorCodeMap[error.code] || 'generic',
      };

      this.hideBackdrop();
      this?.$refs?.paymentBottomSheetError?.open(adoptedError);
      this.stopCheckingIfPaymentSessionExpired();
    },
    stopCheckingIfPaymentSessionExpired() {
      this.checkIfPaymentSessionExpired = false;
      clearTimeout(this.checkIfPaymentSessionExpiredTimeout);
      this.checkIfPaymentSessionExpiredTimeout = null;
    },
    navigateToPaymentStatus({ paymentUuid, origin }) {
      this.paymentAttemptInFlight = true;
      this.$router.replace({
        name: 'PaymentStatus',
        query: {
          origin,
          orderNumber: this.uniqueOrderNumber || this.$route.params.order,
          paymentUuid,
          ...(this.invoiceRequested && {
            invoiceRequested: this.invoiceRequested,
          }),
        },
        params: { session: this.$store.state.session },
      });
      this.hideBackdrop();
    },
    onPaymentMethodChange(method, { userInitiated = true } = {}) {
      this.paymentMethod = method;

      const dataSourceMap = {
        [PAYMENT_METHODS.MONTONIO]: this.montonioPaymentData,
        [PAYMENT_METHODS.STRIPE]: this.stripePaymentData,
        [PAYMENT_METHODS.NEOPAY]: this.neopayPaymentData,
        [PAYMENT_METHODS.NEOPAY_BLIK]: this.neopayBlikPaymentData,
        [PAYMENT_METHODS.MOLLIE_IDEAL]: this.mollieIdealPaymentData,
        [PAYMENT_METHODS.CASH]: this.cashPaymentData,
        [PAYMENT_METHODS.ROOM_BILL]: this.roomBillPaymentData,
        [PAYMENT_METHODS.SOLID_CARD]: this.$store.getters
          .solidGatePaymentIntent,
        [PAYMENT_METHODS.SOLID_WALLET]: this.$store.getters
          .solidGatePaymentIntent,
      };

      const data = dataSourceMap[method];
      if (data) {
        this.subtotal = data.subtotal;
        this.totalAmount =
          method === PAYMENT_METHODS.MOLLIE_IDEAL
            ? parseFloat(data.total)
            : data.total;
        this.transactionFee = data.transaction_fee;
        this.tipsAmount = data.tips;
        this.smallOrderFee = data?.small_order_fee || 0;
      }

      if (userInitiated && method === PAYMENT_METHODS.NEOPAY) {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.scrollToPayCTA();
          });
        });
      }
    },
    async onPayCtaPress(e) {
      e.preventDefault();
      if (this.paymentMethod === PAYMENT_METHODS.STRIPE) {
        this.payByCard(e);
      } else if (this.paymentMethod === PAYMENT_METHODS.MONTONIO) {
        this.sendPurchaseAnalytics(PAYMENT_METHODS.MONTONIO);
        this.navigateToPaymentLink(this.montonioPaymentLink);
      } else if (this.paymentMethod === PAYMENT_METHODS.NEOPAY) {
        this.sendPurchaseAnalytics(PAYMENT_METHODS.NEOPAY);

        // show warning to make sure user is redirected back to the app
        if (this.payGoRef) {
          return this.$refs.pisWarning.open();
        }

        this.navigateToPaymentLink(this.neopayPaymentLink);
      } else if (this.paymentMethod === PAYMENT_METHODS.NEOPAY_BLIK) {
        this.sendPurchaseAnalytics(PAYMENT_METHODS.NEOPAY_BLIK);
        this.navigateToPaymentLink(this.neopayBlikPaymentLink);
      } else if (this.paymentMethod === PAYMENT_METHODS.MOLLIE_IDEAL) {
        this.sendPurchaseAnalytics(PAYMENT_METHODS.MOLLIE_IDEAL);
        this.navigateToPaymentLink(this.mollieIdealPaymentLink);
      } else if (this.paymentMethod === PAYMENT_METHODS.CASH) {
        try {
          await this.$store.dispatch('createCashPaymentCallback', {
            uuid: this.cashPaymentData?.uuid || this.uuid,
            type: 'payment_succeeded',
          });
          this.onPaymentSuccess(this.cashPaymentData?.uuid);
        } catch (error) {
          await this.showBackdrop('error', 2000);
          this.hideBackdrop();
        }
      } else if (this.paymentMethod === PAYMENT_METHODS.ROOM_BILL) {
        try {
          await this.$store.dispatch('createRoomBillPaymentCallback', {
            uuid: this.roomBillPaymentData?.uuid || this.uuid,
            type: 'payment_succeeded',
          });
          this.onPaymentSuccess(this.roomBillPaymentData?.uuid);
        } catch (error) {
          await this.showBackdrop('error', 2000);
          this.hideBackdrop();
        }
      } else if (this.paymentMethod === PAYMENT_METHODS.SOLID_CARD) {
        if (this.solidPaymentsBlocked) {
          return;
        }
        // Sketchy way to submit the form
        this.$refs.paymentMethods.$refs?.solidCardForm?.submit();
      }
    },
    sendPurchaseAnalytics(method) {
      this.$store.dispatch('sendAnalyticsEvent', {
        event: 'purchase',
        transaction_id: this?.uniqueOrderNumber,
        value: this?.totalAmount,
        currency: this.$store.getters.getCurrencyCode,
        method,
      });
    },
    scrollToPayCTA() {
      const ctaElement = this.$refs?.CTAButton?.$el;
      if (!ctaElement) {
        return;
      }

      ctaElement.scrollIntoView({ behavior: 'smooth' });
    },
    async checkIfPaymentSessionExpired() {
      const hidden = document.hidden;
      const notPaymentScreen = this.$route.name !== 'Pay';
      const loading = this.isLoading;
      const backdropVisible = this.backdropVisible;

      if (hidden || notPaymentScreen || loading || backdropVisible) {
        return;
      }

      const onExpired = () => {
        if (hidden || notPaymentScreen || loading || backdropVisible) {
          return;
        }

        this.goBack();
        this.$store.dispatch('showToastInstantly', {
          title: this.$t(
            'screens.payment.error.paymentUnavailableGeneric.title'
          ),
          description: this.$t('screens.payment.paymentSessionExpired'),
        });
      };
      try {
        const paymentUuid =
          this.uuid ||
          this?.stripePaymentData?.uuid ||
          this.cashPaymentData?.uuid ||
          this.roomBillPaymentData?.uuid;

        if (paymentUuid) {
          await this.$store.dispatch('checkIfPaymentIntentExpired', {
            paymentUuid,
            onExpired,
          });
        }
        if (this.neopayPaymentData?.uuid) {
          await this.$store.dispatch('checkIfPaymentIntentExpired', {
            paymentUuid: this.neopayPaymentData?.uuid,
            onExpired,
          });
        }
        if (this.neopayBlikPaymentData?.uuid) {
          await this.$store.dispatch('checkIfPaymentIntentExpired', {
            paymentUuid: this.neopayBlikPaymentData?.uuid,
            onExpired,
          });
        }
      } catch (error) {
        console.log(error);
        onExpired();
      }
    },
    resolveTipAmount(selectedTipAmount) {
      // Picker is on this screen: the user's choice here is authoritative,
      // an explicit 0 ("no tip") included.
      if (this.showTipsPicker) {
        return selectedTipAmount ?? 0;
      }
      // Pay&go bill with no picker: there is nowhere to pick a tip, so never
      // charge one (a stale cart tip from an earlier order must not leak in).
      if (this.isPayGoFlow) {
        return 0;
      }
      // Order flow: the tip was chosen on PlaceOrder and lives in the cart.
      return this.$store.getters.tipAmount || 0;
    },
    async handlePaymentIntents(personnelId, selectedTipAmount) {
      const tipAmount = this.resolveTipAmount(selectedTipAmount);

      await Promise.all([
        this.loadStripeIfNeeded(),
        this.generateStripePaymentIntent({
          personnelId,
          tipAmount,
        }),
        this.generateCashPaymentIntent({
          personnelId,
          tipAmount,
        }),
        this.generateRoomBillPaymentIntent({
          personnelId,
          tipAmount,
        }),
        this.generateSolidPaymentIntent({
          personnelId,
          tipAmount,
        }),
        this.createMontonioOrder({
          personnelId,
          tipAmount,
        }),
        this.createNeopayTransaction({
          personnelId,
          tipAmount,
        }),
        this.createNeopayBlikTransaction({
          personnelId,
          tipAmount,
        }),
        this.createMollieIdealPayment({
          personnelId,
          tipAmount,
        }),
      ]);
      await this.loadStripeElements();
    },
  },
};
</script>

<style lang="scss" scoped>
.payment-container {
  background-color: $color-background-gray;
  height: 100%;
  overflow-anchor: none;
}
</style>
